import React, { useState, useMemo } from 'react';
import {
  MapPin,
  Search,
  CheckCircle2,
  Building2,
  Globe2,
  Users,
  Briefcase,
  Compass,
  Layers,
  ArrowRight,
  ShieldCheck,
} from 'lucide-react';
import {
  ALL_NIGERIAN_STATES,
  NIGERIA_TOTAL_LGAS_COUNT,
  searchNigerianLocations,
  getStateByName,
  getLgasForState,
  getCitiesForLga,
} from '../../data/nigerianLocations';
import { Profile, Job } from '../../types';

interface AdminLocationSectionProps {
  professionals: Profile[];
  jobs: Job[];
}

export const AdminLocationSection: React.FC<AdminLocationSectionProps> = ({
  professionals,
  jobs,
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedStateName, setSelectedStateName] = useState('Lagos');
  const [selectedLgaName, setSelectedLgaName] = useState('Ikeja');

  // Search results
  const searchResults = useMemo(() => {
    if (!searchQuery.trim() || searchQuery.length < 2) return [];
    return searchNigerianLocations(searchQuery, 15);
  }, [searchQuery]);

  const selectedStateObj = useMemo(() => {
    return getStateByName(selectedStateName) || ALL_NIGERIAN_STATES[0];
  }, [selectedStateName]);

  const lgasInSelectedState = useMemo(() => {
    return getLgasForState(selectedStateName);
  }, [selectedStateName]);

  const citiesInSelectedLga = useMemo(() => {
    return getCitiesForLga(selectedStateName, selectedLgaName);
  }, [selectedStateName, selectedLgaName]);

  // Aggregate stats
  const proCountsByState = useMemo(() => {
    const counts: Record<string, number> = {};
    professionals.forEach((p) => {
      if (p.state) {
        counts[p.state] = (counts[p.state] || 0) + 1;
      }
    });
    return counts;
  }, [professionals]);

  const jobCountsByState = useMemo(() => {
    const counts: Record<string, number> = {};
    jobs.forEach((j) => {
      if (j.state) {
        counts[j.state] = (counts[j.state] || 0) + 1;
      }
    });
    return counts;
  }, [jobs]);

  const prosInSelectedLga = useMemo(() => {
    return professionals.filter(
      (p) =>
        p.state === selectedStateName &&
        (p.lga === selectedLgaName ||
          (p.serviceAreas && p.serviceAreas.includes(selectedLgaName)) ||
          (p.serviceLgas && p.serviceLgas.includes(selectedLgaName)))
    );
  }, [professionals, selectedStateName, selectedLgaName]);

  const jobsInSelectedLga = useMemo(() => {
    return jobs.filter(
      (j) => j.state === selectedStateName && j.lga === selectedLgaName
    );
  }, [jobs, selectedStateName, selectedLgaName]);

  return (
    <div className="space-y-6 animate-in fade-in">
      {/* 1. TOP STATS BAR */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4">
        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold mb-1">
            <Globe2 className="w-4 h-4 text-emerald-600" />
            <span>Country</span>
          </div>
          <div className="text-xl sm:text-2xl font-black text-slate-900">Nigeria</div>
          <div className="text-[11px] text-slate-400 mt-0.5">Federal Republic</div>
        </div>

        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold mb-1">
            <Building2 className="w-4 h-4 text-blue-600" />
            <span>States + FCT</span>
          </div>
          <div className="text-xl sm:text-2xl font-black text-slate-900">
            {ALL_NIGERIAN_STATES.length}
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">36 States & Abuja FCT</div>
        </div>

        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold mb-1">
            <Layers className="w-4 h-4 text-indigo-600" />
            <span>Total Official LGAs</span>
          </div>
          <div className="text-xl sm:text-2xl font-black text-slate-900">
            {NIGERIA_TOTAL_LGAS_COUNT}
          </div>
          <div className="text-[11px] text-emerald-600 font-medium mt-0.5">
            100% Authoritative Data
          </div>
        </div>

        <div className="bg-white p-4 sm:p-5 rounded-2xl border border-slate-200 shadow-xs">
          <div className="flex items-center gap-2 text-slate-500 text-xs font-semibold mb-1">
            <Users className="w-4 h-4 text-amber-600" />
            <span>Active Artisans</span>
          </div>
          <div className="text-xl sm:text-2xl font-black text-slate-900">
            {professionals.length}
          </div>
          <div className="text-[11px] text-slate-400 mt-0.5">
            Across {Object.keys(proCountsByState).length} States
          </div>
        </div>
      </div>

      {/* 2. FAST GLOBAL LOCATION LOOKUP */}
      <div className="bg-white rounded-3xl p-5 sm:p-6 border border-slate-200 shadow-xs space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
          <div>
            <h3 className="text-base font-bold text-slate-900 flex items-center gap-2">
              <Compass className="w-5 h-5 text-emerald-600" />
              Administrative Location Search & Verification Engine
            </h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Verify that any LGA, city, or area belongs strictly to the correct Nigerian State.
            </p>
          </div>
          <span className="text-[11px] text-slate-500 bg-slate-100 px-3 py-1 rounded-full font-medium self-start sm:self-auto">
            Zero Hallucination Guarantee
          </span>
        </div>

        <div className="relative">
          <Search className="w-4 h-4 text-slate-400 absolute left-3.5 top-3.5" />
          <input
            type="text"
            placeholder="Search any Nigerian LGA, town, capital or area (e.g. Asaba, Ikeja, Wuse, Oshimili, Alaba)..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full pl-10 pr-4 py-2.5 bg-slate-50 border border-slate-200 rounded-xl text-xs sm:text-sm focus:outline-hidden focus:border-emerald-500 focus:bg-white"
          />
        </div>

        {/* Search Results Dropdown / Panel */}
        {searchQuery.trim().length >= 2 && (
          <div className="border border-slate-200 rounded-2xl p-3 bg-slate-50/50 space-y-2 max-h-60 overflow-y-auto">
            <div className="text-[11px] font-bold text-slate-500 px-1">
              Search Matches ({searchResults.length}):
            </div>
            {searchResults.length === 0 ? (
              <div className="text-xs text-slate-400 p-2">
                No Nigerian LGA or area found matching "{searchQuery}".
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {searchResults.map((res, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      setSelectedStateName(res.state);
                      setSelectedLgaName(res.lga);
                      setSearchQuery('');
                    }}
                    className="p-2.5 rounded-xl bg-white border border-slate-200 hover:border-emerald-500 hover:bg-emerald-50/30 text-left transition-all flex items-center justify-between group"
                  >
                    <div>
                      <div className="font-bold text-xs text-slate-900 group-hover:text-emerald-700">
                        {res.cityArea ? `${res.cityArea} (${res.lga})` : res.lga}
                      </div>
                      <div className="text-[11px] text-slate-500">
                        {res.state} State • Nigeria
                      </div>
                    </div>
                    <ArrowRight className="w-3.5 h-3.5 text-slate-300 group-hover:text-emerald-600 transition-colors" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* 3. INTERACTIVE STATE & LGA BROWSER */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column: States List */}
        <div className="bg-white rounded-3xl p-5 border border-slate-200 shadow-xs space-y-3">
          <div className="flex items-center justify-between pb-2 border-b border-slate-100">
            <span className="font-bold text-slate-900 text-sm">36 States + FCT</span>
            <span className="text-[11px] text-slate-400 font-medium">
              {ALL_NIGERIAN_STATES.length} Entities
            </span>
          </div>

          <div className="space-y-1 max-h-[500px] overflow-y-auto pr-1">
            {ALL_NIGERIAN_STATES.map((st) => {
              const isSelected = st.name === selectedStateName;
              const proCount = proCountsByState[st.name] || 0;
              const jobCount = jobCountsByState[st.name] || 0;

              return (
                <button
                  key={st.name}
                  onClick={() => {
                    setSelectedStateName(st.name);
                    const lgas = getLgasForState(st.name);
                    if (lgas.length > 0) {
                      setSelectedLgaName(lgas[0]);
                    }
                  }}
                  className={`w-full text-left p-2.5 rounded-xl text-xs font-semibold transition-all flex items-center justify-between ${
                    isSelected
                      ? 'bg-slate-900 text-white shadow-xs'
                      : 'hover:bg-slate-100 text-slate-700'
                  }`}
                >
                  <div>
                    <div className="font-bold">{st.name}</div>
                    <div
                      className={`text-[10px] ${
                        isSelected ? 'text-slate-300' : 'text-slate-400'
                      }`}
                    >
                      {getLgasForState(st.name).length} LGAs {st.capital ? `• Cap: ${st.capital}` : ''}
                    </div>
                  </div>

                  <div className="flex items-center gap-1.5 text-[10px]">
                    {proCount > 0 && (
                      <span
                        className={`px-1.5 py-0.5 rounded-md ${
                          isSelected
                            ? 'bg-emerald-500/30 text-emerald-300'
                            : 'bg-emerald-50 text-emerald-700'
                        }`}
                      >
                        {proCount} Pros
                      </span>
                    )}
                    {jobCount > 0 && (
                      <span
                        className={`px-1.5 py-0.5 rounded-md ${
                          isSelected
                            ? 'bg-blue-500/30 text-blue-300'
                            : 'bg-blue-50 text-blue-700'
                        }`}
                      >
                        {jobCount} Jobs
                      </span>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Columns: Selected State Details & LGAs */}
        <div className="lg:col-span-2 space-y-4">
          {/* State Summary Banner */}
          <div className="bg-slate-900 text-white rounded-3xl p-5 sm:p-6 border border-slate-800 space-y-3">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2">
              <div>
                <span className="text-[10px] uppercase font-bold text-emerald-400 tracking-wider">
                  State Administrative Inspector
                </span>
                <h2 className="text-xl sm:text-2xl font-black text-white">
                  {selectedStateObj.name} State
                </h2>
              </div>
              <div className="flex items-center gap-2">
                <span className="bg-slate-800 border border-slate-700 px-3 py-1 rounded-xl text-xs font-semibold text-slate-200">
                  {lgasInSelectedState.length} Official LGAs
                </span>
                {selectedStateObj.capital && (
                  <span className="bg-emerald-500/20 border border-emerald-500/40 px-3 py-1 rounded-xl text-xs font-semibold text-emerald-400">
                    Capital: {selectedStateObj.capital}
                  </span>
                )}
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3 pt-2 border-t border-slate-800 text-xs">
              <div>
                <span className="text-slate-400 block text-[10px]">Registered Artisans</span>
                <span className="text-base font-extrabold text-emerald-400">
                  {proCountsByState[selectedStateName] || 0}
                </span>
              </div>
              <div>
                <span className="text-slate-400 block text-[10px]">Customer Job Posts</span>
                <span className="text-base font-extrabold text-blue-400">
                  {jobCountsByState[selectedStateName] || 0}
                </span>
              </div>
              <div>
                <span className="text-slate-400 block text-[10px]">LGA Coverage Status</span>
                <span className="text-base font-extrabold text-white">
                  Active
                </span>
              </div>
            </div>
          </div>

          {/* LGAs Grid in Selected State */}
          <div className="bg-white rounded-3xl p-5 border border-slate-200 shadow-xs space-y-3">
            <div className="flex items-center justify-between">
              <h4 className="font-bold text-slate-900 text-xs sm:text-sm">
                Local Government Areas in {selectedStateName}
              </h4>
              <span className="text-[11px] text-slate-400">
                Click any LGA to view coverage & artisans
              </span>
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-56 overflow-y-auto pr-1">
              {lgasInSelectedState.map((lga) => {
                const isSelected = lga === selectedLgaName;
                return (
                  <button
                    key={lga}
                    onClick={() => setSelectedLgaName(lga)}
                    className={`p-2.5 rounded-xl text-left text-xs font-semibold border transition-all ${
                      isSelected
                        ? 'bg-emerald-600 text-white border-emerald-600 shadow-xs'
                        : 'bg-slate-50 text-slate-700 border-slate-200 hover:border-slate-300 hover:bg-white'
                    }`}
                  >
                    <div className="truncate">{lga}</div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Selected LGA Deep Dive */}
          <div className="bg-white rounded-3xl p-5 border border-slate-200 shadow-xs space-y-4">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-emerald-600" />
                <span className="font-bold text-slate-900 text-sm">
                  {selectedLgaName} LGA ({selectedStateName} State)
                </span>
              </div>
              <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2.5 py-0.5 rounded-full border border-emerald-200">
                {prosInSelectedLga.length} Artisans Available
              </span>
            </div>

            {/* Recognized Areas / Districts */}
            {citiesInSelectedLga.length > 0 && (
              <div>
                <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide block mb-1.5">
                  Recognized Commercial & Residential Areas:
                </span>
                <div className="flex flex-wrap gap-1.5">
                  {citiesInSelectedLga.map((c) => (
                    <span
                      key={c}
                      className="px-2.5 py-1 rounded-lg text-xs bg-slate-100 text-slate-700 border border-slate-200 font-medium"
                    >
                      {c}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {/* Artisans in this LGA */}
            <div>
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide block mb-1.5">
                Artisans Serving this LGA ({prosInSelectedLga.length}):
              </span>
              {prosInSelectedLga.length === 0 ? (
                <div className="text-xs text-slate-400 p-3 bg-slate-50 rounded-xl">
                  No artisans currently registered with primary base or coverage in {selectedLgaName}.
                </div>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                  {prosInSelectedLga.map((p) => (
                    <div
                      key={p.id}
                      className="p-2.5 rounded-xl border border-slate-200 bg-slate-50/50 flex items-center justify-between text-xs"
                    >
                      <div>
                        <div className="font-bold text-slate-900">{p.fullName}</div>
                        <div className="text-[11px] text-slate-500">
                          {p.primaryCategory || 'Artisan'} • {p.cityArea || p.lga}
                        </div>
                      </div>
                      <span className="text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-md">
                        {p.isAvailable ? 'Available' : 'Busy'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Jobs in this LGA */}
            <div>
              <span className="text-[11px] font-bold text-slate-500 uppercase tracking-wide block mb-1.5">
                Active Customer Job Requests in {selectedLgaName} ({jobsInSelectedLga.length}):
              </span>
              {jobsInSelectedLga.length === 0 ? (
                <div className="text-xs text-slate-400 p-3 bg-slate-50 rounded-xl">
                  No active job requests in {selectedLgaName} at this moment.
                </div>
              ) : (
                <div className="space-y-2">
                  {jobsInSelectedLga.map((j) => (
                    <div
                      key={j.id}
                      className="p-3 rounded-xl border border-slate-200 bg-slate-50/50 flex items-center justify-between text-xs"
                    >
                      <div>
                        <div className="font-bold text-slate-900">{j.title}</div>
                        <div className="text-[11px] text-slate-500">
                          {j.categoryName} • ₦{(j.budgetEstimate || 0).toLocaleString()} • {j.urgency}
                        </div>
                      </div>
                      <span className="text-[10px] font-bold text-slate-700 bg-white border border-slate-200 px-2 py-1 rounded-md">
                        {j.quotesCount || 0} Quotes
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
